import { default as React, useState } from "react";
import Moment from "moment";
import { Row, Col, Button, ListGroup, Alert, Modal } from "react-bootstrap";
import { PacmanLoader } from "react-spinners";
import { GenericTable } from "components/GenericTable";
import EditableCell from "containers/common/EditableCell";
import { createColumnHelper } from "@tanstack/react-table";
import "containers/common/List.css";
import "containers/common/Table.css";

function BuildingGradeList({
  data,
  isLoading,
  isAuthenticated,
  controlType,
  saveRecord,
  deleteRecord,
  updateTableState,
  buildingGradeSourceOptions,
  buildingGradeOptions,
  filter,
  user,
}) {
  const dataSet = "building_grades";
  const tableData = data || [];
  const columnHelper = createColumnHelper();
  const [showAlert, setShowAlert] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  function validateRows(rows) {
    for (const row of rows) {
      if (String(row?.building_grade_code ?? "") === "") {
        return "Building Grade cannot be blank. The record has not been saved";
      }
    }

    return null;
  }

  function handleDeleteRow(row, event) {
    event.preventDefault();
    const confirmed = window.confirm("Are you sure you want to delete this record?");
    if (!confirmed) {
      return;
    }
    deleteRecord(dataSet, row.original);
  }

  const columns = [
    columnHelper.accessor("building_grade_id", {
      id: "building_grade_id",
      header: "ID",
      size: 60,
    }),
    columnHelper.accessor("building_grade_code", {
      id: "building_grade_code",
      header: "Building Grade",
      cell: EditableCell,
      meta: {
        options: buildingGradeOptions || [],
        useClearableSingleSelect: true,
        saveOnBlurOnly: true,
      },
    }),
    columnHelper.accessor("building_grade_source", {
      id: "building_grade_source",
      header: "Building Grade Source",
    }),
    columnHelper.accessor("source_date", {
      id: "source_date",
      header: "Source Date",
      cell: EditableCell,
    }),
    columnHelper.accessor("override", {
      id: "override",
      header: "Override",
      cell: EditableCell,
      size: 85,
    }),
    columnHelper.accessor("notes", {
      id: "notes",
      header: "Notes",
      cell: EditableCell,
    }),
    columnHelper.accessor("source_method", {
      id: "source_method",
      header: "Source Method",
    }),
    columnHelper.accessor("delete_building_grade_id", {
      id: "delete_building_grade_id",
      header: "",
      cell: ({ row }) => (
        <Button type="button" size="sm" variant="danger"
          onClick={(event) => handleDeleteRow(row, event)}>
          Delete
        </Button>
      ),
      size: 65,
    }),
  ];

  const tableMeta = {
    updateData: (rowIndex, columnId, value) => {
      updateTableState(dataSet, rowIndex, columnId, value);
    },
    shouldValidateUnchangedOnBlur: ({ column }) => column?.id === "building_grade_code",
    revertOnFailedSave: true,
    handleSave: async ({ row, column, value }) => {
      const selectedBuildingGradeOption =
        column?.id === "building_grade_code"
          ? (buildingGradeOptions || []).find((option) => option.value === value)
          : null;

      const record = {
        ...(data?.[row.index] || {}),
        ...row.original,
        ...(column?.id ? { [column.id]: value } : {}),
        ...(selectedBuildingGradeOption
          ? { building_grade: selectedBuildingGradeOption.label }
          : {}),
      };

      const updatedRows = [...(data || [])];
      updatedRows[row.index] = record;
      const validationMessage = validateRows(updatedRows);
      if (validationMessage) {
        setErrorMessage(validationMessage);
        setShowAlert(true);
        return false;
      }

      try {
        await saveRecord(dataSet, record);
        console.log("Record saved successfully via parent logic");
      } catch (err) {
        console.error("Save failed:", err);
      }
    },
    handleDelete: ({ row }) => {
      deleteRecord(dataSet, row.original);
    },
  };

  // --- Data Fetching and Update Functions ---
  async function addRecord(event) {
    let localData = [...data];
    //Minimum record to create (all not nulls populated)
    localData.push({
      _client_id: `building-grade-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      property_id: filter.propertyid,
      building_grade_code: null,
      building_grade: null,
      building_grade_source_code: "LIST",
      building_grade_source: "Sale/Lease Listing",
      source_method: "KEYED",
      source_date: new Date(),
      override: false,
      notes: "",
      created_by: user,
      last_edited_by: user,
      new_record: true
    });
    updateTableState(localData, dataSet);
  }

  // --- Render Functions ---
  function renderList(data) {
    if (!data) {
      return null
    }
    else {
      return [{}].concat(data).map(
        (d, i) =>
          i !== 0
            ?
            <ListGroup.Item key={i} as="a" header={d.building_grade_code}>
              {d.building_grade_code + " Grade - " + Moment(d.source_date).local().format("DD-MM-YYYY")
              }
            </ListGroup.Item>
            : ""
      );
    }
  }

  function renderComponent() {
    return (
      <div className="Buildinggrades">
        <Modal show={showAlert} onHide={() => setShowAlert(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>Uh Oh!</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Alert variant="danger">{errorMessage}</Alert>
          </Modal.Body>
        </Modal>
        {controlType === "table" && (
          <Row className="show-Container">
            <Col className="text-right">
              <Button variant="secondary" size="sm" onClick={addRecord}>Add</Button>
            </Col>
          </Row>
        )}
        <Row className="show-Container">
          <Col sm={12} md={12} lg={12}>
            {isLoading ? (
              <div className="outer-div">
                <div className="sweet-loading inner-div">
                  <PacmanLoader
                    cssOverride={{ display: "block", margin: "0 auto" }}
                    size={20}
                    color={"#e03c31"}
                    loading={isLoading}
                  />
                </div>
              </div>
            ) : controlType === "table" ? (
              <GenericTable
                columns={columns}
                data={tableData}
                section="building-grade"
                pageSize={10}
                meta={tableMeta}
              />
            ) : renderList(data)}
          </Col>
        </Row>
      </div>
    );
  }
  return (
    <div className="BuildingGradeList">
      {isAuthenticated ? renderComponent() : console.log("User not authenticated.")}
    </div>
  );
}

export default BuildingGradeList;